import { GetRolesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/inputs/get-roles-paginated.model';
import { GatewayRoleModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/output/gateway-role.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  ROLE_FILTERS_LSK,
  ROLE_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  ROLE_LIST_DEFAULT_OPTIONS,
  ROLE_LIST_FILTER_NAME_LIST,
  ROLE_LIST_SORT_FIELD,
  ROLE_LIST_SORT_ORDER,
  RoleListOptionsModel,
} from './role-list/role-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const RoleContext = createContext<GatewayRoleModel | null>(null);

export const useRoleContext = (): GatewayRoleModel => {
  const context = useContext(RoleContext);

  if (!context) {
    throw new Error('useRoleContext must be used within RoleContext.Provider');
  }

  return context;
};

interface RoleContextProviderProps {
  data: GatewayRoleModel;
  children: React.ReactNode;
}

export const RoleContextProvider = ({
  data,
  children,
}: RoleContextProviderProps): React.JSX.Element => {
  return <RoleContext.Provider value={data}>{children}</RoleContext.Provider>;
};

// /////////////////////////////////////////////////////////////////////////////

export interface RoleListContextProps {
  getPaginatedQueryParameters: GetRolesPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetRolesPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: RoleListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<RoleListOptionsModel>>;
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const RoleListContext = createContext<RoleListContextProps | null>(null);

export const useRoleListContext = (): RoleListContextProps => {
  const context = useContext(RoleListContext);

  if (!context) {
    throw new Error(
      'useRoleListContext must be used within RoleListContext.Provider',
    );
  }

  return context;
};

interface RoleListContextProviderProps {
  children: React.ReactNode;
}

export const RoleListContextProvider = ({
  children,
}: RoleListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetRolesPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        customerId: [],
        networkId: [],
        textToSearch: '',
        // Sortable fields
        sortField: ROLE_LIST_SORT_FIELD,
        sortOrder: ROLE_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...ROLE_LIST_FILTER_NAME_LIST,
      ],
      ROLE_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<RoleListOptionsModel>(
      ROLE_LIST_DEFAULT_OPTIONS,
      ROLE_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    columnOptions,
    setColumnOptions,
    // setGetPaginatedLimitList,
  ]);

  return (
    <RoleListContext.Provider value={data}>{children}</RoleListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterRoleListContext =
  createContext<DeepPartialSkipArrayKey<GetRolesPaginatedModel> | null>(null);

export const useFilterRoleListContext =
  (): DeepPartialSkipArrayKey<GetRolesPaginatedModel> => {
    const context = useContext(FilterRoleListContext);

    if (!context) {
      throw new Error(
        'useFilterRoleListContext must be used within FilterRoleListContext.Provider',
      );
    }

    return context;
  };

interface FilterRoleListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetRolesPaginatedModel>;
  children: React.ReactNode;
}

export const FilterRoleListContextProvider = ({
  data,
  children,
}: FilterRoleListContextProviderProps): React.JSX.Element => {
  return (
    <FilterRoleListContext.Provider value={data}>
      {children}
    </FilterRoleListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchRoleListContext =
  createContext<DeepPartialSkipArrayKey<GetRolesPaginatedModel> | null>(null);

export const useFilterSearchRoleListContext =
  (): DeepPartialSkipArrayKey<GetRolesPaginatedModel> => {
    const context = useContext(FilterSearchRoleListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchRoleListContext must be used within FilterSearchRoleListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchRoleListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetRolesPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchRoleListContextProvider = ({
  data,
  children,
}: FilterSearchRoleListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchRoleListContext.Provider value={data}>
      {children}
    </FilterSearchRoleListContext.Provider>
  );
};
